import React, { useEffect, useState } from "react";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Trash2,
  XCircle,
  Activity,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProviderIcon } from "@/components/ProviderIcons";

/**
 * Generic, two-mode panel for LLM API keys.
 *
 * Props:
 *  - providers: list of provider catalogue rows (id, label, default_model, models, key_prefix, get_key_url, doc_url)
 *  - rows: list of { provider, has_key, api_key_masked, model, last_checked, label, default_model }
 *  - onSave({provider, api_key, model}): persist + return updated row
 *  - onDelete({provider}): remove
 *  - onCheck({provider, api_key?, model?}): validate against provider (api_key omitted = check stored)
 *  - title, subtitle: section copy
 *  - mode: "global" | "agent" — purely cosmetic
 */
function ProviderCard({ provider, row, onSave, onDelete, onCheck, mode = "global" }) {
  const stored = row?.has_key === true;
  const [editing, setEditing] = useState(!stored);
  const [keyValue, setKeyValue] = useState("");
  const [model, setModel] = useState(row?.model || provider.default_model);
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(row?.last_checked || null);

  useEffect(() => {
    setEditing(!row?.has_key);
    setCheck(row?.last_checked || null);
    setModel(row?.model || provider.default_model);
    setKeyValue("");
    setShowKey(false);
  }, [row?.has_key, row?.api_key_masked, row?.model, row?.last_checked, provider.default_model]);

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!keyValue.trim()) {
      toast.error("Ingrese una API key.");
      return;
    }
    try {
      setSaving(true);
      await onSave({ provider: provider.id, api_key: keyValue.trim(), model });
      toast.success(`Clave de ${provider.label} guardada (cifrada).`);
      setKeyValue("");
      setEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la clave.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete({ provider: provider.id });
      toast.success(`Clave de ${provider.label} eliminada.`);
      setCheck(null);
      setEditing(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar.");
    }
  };

  const handleCheck = async () => {
    try {
      setChecking(true);
      const body = keyValue.trim() ? { api_key: keyValue.trim(), model } : { model };
      const r = await onCheck({ provider: provider.id, ...body });
      setCheck({ ...r, at: new Date().toISOString() });
      if (r.ok) toast.success(`${provider.label}: clave válida${r.model ? ` (${r.model})` : ""}.`);
      else toast.error(`${provider.label}: ${r.message || "clave inválida"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al verificar la clave.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      className="border border-slate-200 bg-white p-5"
      data-testid={`llm-card-${mode}-${provider.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-slate-100">
            <ProviderIcon provider={provider.id} className="h-7 w-7" />
          </div>
          <div>
            <p className="font-display text-base text-slate-900">{provider.label}</p>
            <p className="text-[11px] text-slate-500">
              {provider.company}
              {provider.get_key_url && (
                <>
                  {" · "}
                  <a
                    href={provider.get_key_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-accent-blue hover:underline"
                  >
                    Obtener API key aquí <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </p>
          </div>
        </div>
        {stored && (
          <span
            className="border border-teal-700/40 bg-teal-50 px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] text-teal-800"
            data-testid={`llm-card-status-${mode}-${provider.id}`}
          >
            Conectado
          </span>
        )}
      </div>

      <form onSubmit={handleSave} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          {stored && !editing ? (
            <Input
              readOnly
              value={row?.api_key_masked || ""}
              className="rounded-sm bg-slate-50 font-mono text-sm"
              data-testid={`llm-card-key-masked-${mode}-${provider.id}`}
            />
          ) : (
            <Input
              type={showKey ? "text" : "password"}
              value={keyValue}
              onChange={(e) => setKeyValue(e.target.value)}
              placeholder={provider.key_prefix ? `${provider.key_prefix}xxxxxxxxxxxxxxxxx` : "Pegue su API key…"}
              className="rounded-sm font-mono text-sm pr-10"
              autoComplete="off"
              data-testid={`llm-card-key-input-${mode}-${provider.id}`}
            />
          )}
          {!stored || editing ? (
            <button
              type="button"
              onClick={() => setShowKey((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              tabIndex={-1}
              aria-label={showKey ? "Ocultar clave" : "Mostrar clave"}
            >
              {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          ) : null}
        </div>

        {stored && !editing ? (
          <>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]"
              onClick={() => setEditing(true)}
              data-testid={`llm-card-edit-btn-${mode}-${provider.id}`}
            >
              Cambiar clave
            </Button>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm border-red-300 text-[11px] uppercase tracking-[0.18em] text-red-600 hover:bg-red-50"
              onClick={handleDelete}
              data-testid={`llm-card-delete-btn-${mode}-${provider.id}`}
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              Eliminar
            </Button>
          </>
        ) : (
          <>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.18em] text-white hover:bg-slate-700"
              data-testid={`llm-card-save-btn-${mode}-${provider.id}`}
            >
              {saving ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-2 h-3.5 w-3.5" />}
              Guardar
            </Button>
            {stored && (
              <Button
                type="button"
                variant="outline"
                className="rounded-sm border-slate-300 text-[11px] uppercase tracking-[0.18em]"
                onClick={() => {
                  setEditing(false);
                  setKeyValue("");
                }}
              >
                Cancelar
              </Button>
            )}
          </>
        )}
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Modelo:</label>
        <select
          value={model}
          onChange={(e) => setModel(e.target.value)}
          className="border border-slate-300 bg-white px-2 py-1 text-xs"
          data-testid={`llm-card-model-${mode}-${provider.id}`}
        >
          {[...new Set([provider.default_model, ...(provider.models || []), model].filter(Boolean))].map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleCheck}
          disabled={checking || (!stored && !keyValue.trim())}
          className="ml-auto inline-flex items-center gap-1 text-xs text-accent-blue hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          data-testid={`llm-card-check-btn-${mode}-${provider.id}`}
        >
          {checking ? <Loader2 className="h-3 w-3 animate-spin" /> : <Activity className="h-3 w-3" />}
          {checking ? "Verificando…" : "Comprobar clave API"}
        </button>
      </div>

      {check && (
        <div
          className={`mt-3 flex items-start gap-2 border ${
            check.ok ? "border-teal-700/40 bg-teal-50" : "border-red-300 bg-red-50"
          } px-3 py-2 text-[11px] leading-relaxed`}
          data-testid={`llm-card-check-result-${mode}-${provider.id}`}
        >
          {check.ok ? (
            <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-blue" />
          ) : (
            <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-600" />
          )}
          <div className="min-w-0">
            <p className={check.ok ? "font-medium text-teal-900" : "font-medium text-red-900"}>
              {check.ok ? "Clave válida" : "Clave inválida"}
              {check.model ? <span className="ml-2 font-mono text-[10px] text-slate-600">{check.model}</span> : null}
              {typeof check.latency_ms === "number" ? (
                <span className="ml-2 font-mono text-[10px] text-slate-500">{check.latency_ms} ms</span>
              ) : null}
            </p>
            {check.message && <p className="mt-1 break-words text-slate-700">{check.message}</p>}
            {check.at && (
              <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-slate-500">
                Última verificación: {new Date(check.at).toLocaleString("es-419")}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function LlmKeysPanel({
  providers,
  rows,
  onSave,
  onDelete,
  onCheck,
  title = "Modelos LLM",
  subtitle,
  mode = "global",
}) {
  const rowByProvider = Object.fromEntries((rows || []).map((r) => [r.provider, r]));
  return (
    <section data-testid={`llm-keys-panel-${mode}`}>
      <div className="border-b border-slate-200 pb-4">
        <h2 className="font-display text-2xl text-slate-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
      </div>
      <div className="mt-6 grid gap-4 md:grid-cols-2">
        {providers.map((p) => (
          <ProviderCard
            key={p.id}
            provider={p}
            row={rowByProvider[p.id]}
            onSave={onSave}
            onDelete={onDelete}
            onCheck={onCheck}
            mode={mode}
          />
        ))}
      </div>
    </section>
  );
}
